import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { deleteSubs, fetchAllSubscriptions } from '../../services/Common.ts';
import { FaCrown } from 'react-icons/fa';
import { MdCircle, MdDelete, MdOnlinePrediction } from 'react-icons/md';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from '../../theme.ts';

interface Subscription {
  id: number;
  title: string;
  description: string;
  key_points: string[];
  offer_price: number;
  code: string;
  type: string;
  duration: string;
  limit: number;
  active: boolean;
}

const AdminSubscription = () => {
  const { themeName } = useTheme();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    key_points: [''],
    offer_price: '',
    code: '',
    type: '',
    duration: '',
    limit: '',
    active: true,
  });
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('');
  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;
  const [deletingPlanId, setDeletingPlanId] = useState<number | null>(null); // Track the plan being deleted

  const deleteSubscription = async (id: number) => {
    setDeletingPlanId(id); // Set the current plan's ID
    try {
      const response: any = await deleteSubs(token, id);
      console.log('Deleted:', response.data);
      fetchSubscriptions(); // Refresh the subscription list
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingPlanId(null); // Reset the deleting state
    }
  };

  const fetchSubscriptions = async () => {
    setDataLoading(true)
    try {
      const response: any = await fetchAllSubscriptions(token)
      // console.log('resp', response)
      setSubscriptions(response.data)
      setDataLoading(false)
    } catch (err) {
      console.log(err)
      setDataLoading(false)
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handleCreateSubscription = () => {
    setIsEditing(false);
    setFormData({
      title: '',
      description: '',
      key_points: [''],
      offer_price: '',
      code: '',
      type: '',
      duration: '',
      limit: '',
      active: true,
    });
    setIsModalOpen(true);
  };

  const handleEditSubscription = (subscription: Subscription) => {
    setIsEditing(true);
    setSelectedSubscriptionId(subscription.id);
    console.log('subscription', subscription.id)
    setFormData({
      title: subscription.title,
      description: subscription.description,
      key_points: subscription.key_points || [''],
      offer_price: String(subscription.offer_price),
      code: subscription.code,
      type: subscription.type,
      duration: subscription.duration,
      limit: String(subscription.limit),
      active: subscription.active,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      title: '',
      description: '',
      key_points: [''],
      offer_price: '',
      code: '',
      type: '',
      duration: '',
      limit: '',
      active: true,
    });
    setErrorMessage('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyPointChange = (index: number, value: string) => {
    const updatedKeyPoints = [...formData.key_points];
    updatedKeyPoints[index] = value;
    setFormData((prev) => ({ ...prev, key_points: updatedKeyPoints }));
  };

  const handleAddKeyPoint = () => {
    setFormData((prev) => ({ ...prev, key_points: [...prev.key_points, ''] }));
  };

  const handleRemoveKeyPoint = (index: number) => {
    const updatedKeyPoints = formData.key_points.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, key_points: updatedKeyPoints }));
  };

  const handleSubmit = async () => {
    const subsdata = new FormData();
    subsdata.append('title', formData.title);
    subsdata.append('description', formData.description);
    subsdata.append('key_points', JSON.stringify(formData.key_points)); // Serialize key_points as JSON
    subsdata.append('offer_price', String(formData.offer_price));
    subsdata.append('code', formData.code);
    subsdata.append('type', formData.type);
    subsdata.append('duration', formData.duration);
    subsdata.append('limit', String(formData.limit));
    subsdata.append('active', String(formData.active));

    setIsLoading(true);
    setErrorMessage('');
    try {
      if (isEditing && selectedSubscriptionId) {
        // Update existing subscription
        await axios.patch(
          `${process.env.REACT_APP_DB_URI}/users/subscription/${selectedSubscriptionId}/`,
          subsdata,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        fetchSubscriptions(); // Refresh the subscription list
      } else {
        // Create new subscription
        const response = await axios.post(
          `${process.env.REACT_APP_DB_URI}/users/subscription/`,
          subsdata,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSubscriptions((prev) => [...prev, response.data]);
      }
      handleCloseModal();
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.response?.data?.message || 'Failed to save subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: themes[themeName].colors.background }}
    >
      {/* <h1 className="text-2xl font-bold text-[#1cbfbf] mb-6">Manage Subscriptions</h1> */}

      {/* Create Subscription Button */}
      <div className="mb-6">
        <button
          onClick={handleCreateSubscription}
          className="px-4 py-2 rounded-lg shadow-md hover:opacity-90 transition"
          style={{
            backgroundColor: themes[themeName].colors.primary,
            color: '#FFFFFF'
          }}
        >
          Create New Subscription Plan
        </button>
      </div>

      {/* Subscription Cards */}
      {dataLoading ?
        <div className="flex justify-center items-center mt-6">
          <div
            className="border-t-4 rounded-full w-12 h-12 animate-spin"
            style={{ borderColor: themes[themeName].colors.primary }}
          ></div>
          <p
            className="ml-4"
            style={{ color: themes[themeName].colors.primary }}
          >
            Loading...
          </p>
        </div>
        :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptions?.map((sub) => (
            <div
              key={sub.id}
              className="shadow-md rounded-lg p-4"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderColor: themes[themeName].colors.border,
                borderWidth: 1,
                borderStyle: 'solid'
              }}
            >
              <h2
                className="text-xl font-bold mb-2"
                style={{ color: themes[themeName].colors.text }}
              >
                {sub?.title}
              </h2>
              <p
                className="text-lg font-semibold mb-4"
                style={{ color: themes[themeName].colors.primary }}
              >
                {sub?.offer_price}
                <span className='text-xs lowercase ml-1' style={{ color: themes[themeName].colors.primary }}>
                  /{sub?.duration}
                </span>
              </p>
              <ul
                className="text-sm mb-4"
                style={{ color: themes[themeName].colors.textSecondary }}
              >
                {sub?.key_points?.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <span
                      className="mr-2"
                      style={{ color: themes[themeName].colors.success }}
                    >
                      ✔
                    </span> {feature}
                  </li>
                ))}
              </ul>
              <div className="flex text-xs gap-2 mb-2 items-center">
                <span style={{ color: themes[themeName].colors.primary }}>Type : </span>
                <span className='lowercase' style={{ color: themes[themeName].colors.textSecondary }}> {sub?.type}</span>
                <span style={{ color: themes[themeName].colors.warning }}>
                  <FaCrown />
                </span>
              </div>
              <div className="flex text-xs gap-2 mb-2 items-center">
                <span className='font-semibold' style={{ color: themes[themeName].colors.text }}>Status : </span>
                <span className='lowercase' style={{ color: sub?.active ? themes[themeName].colors.success : themes[themeName].colors.error }}> {sub?.active ? "Active" : "InActive"}</span>
                <span style={{ color: themes[themeName].colors.warning }}>
                  {
                    sub?.active ?
                      <MdOnlinePrediction />
                      :
                      <MdCircle />
                  }
                </span>
              </div>
              <div className="flex items-center justify-between">
                <button
                  className="px-3 py-1 rounded-lg hover:opacity-90 transition"
                  style={{
                    backgroundColor: themes[themeName].colors.primary,
                    color: '#FFFFFF'
                  }}
                  onClick={() => handleEditSubscription(sub)}
                >
                  Edit Plan
                </button>
                <button
                  disabled={deletingPlanId === sub?.id}
                  className="w-[max-content] py-2 px-3 rounded-lg text-sm font-semibold"
                  style={{
                    backgroundColor: themes[themeName].colors.error,
                    color: '#FFFFFF',
                    cursor: deletingPlanId === sub?.id ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => deleteSubscription(sub?.id)}
                >
                  {deletingPlanId === sub?.id ? 'Deleting...' : <MdDelete />}
                </button>
              </div>
            </div>
          ))}
        </div>
      }

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[101] px-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        >
          <div
            className="w-[70%] max-h-[90%] overflow-y-auto p-6 rounded-lg shadow-lg"
            style={{
              backgroundColor: themes[themeName].colors.surface,
              borderColor: themes[themeName].colors.border,
              borderWidth: 1,
              borderStyle: 'solid'
            }}
          >
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: themes[themeName].colors.text }}
            >
              {isEditing ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Title"
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description"
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                />
              </div>
              <div className="col-span-2">
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Key Points
                </label>
                {formData.key_points.map((point, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={point}
                      onChange={(e) => handleKeyPointChange(index, e.target.value)}
                      placeholder="Key Point"
                      className="w-full p-2 rounded"
                      style={{
                        backgroundColor: themes[themeName].colors.background,
                        borderColor: themes[themeName].colors.border,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        color: themes[themeName].colors.text
                      }}
                    />
                    <button
                      onClick={() => handleRemoveKeyPoint(index)}
                      className="px-2 py-1 rounded"
                      style={{
                        backgroundColor: themes[themeName].colors.error,
                        color: '#FFFFFF'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddKeyPoint}
                  className="px-3 py-1 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.primary,
                    color: '#FFFFFF'
                  }}
                >
                  Add Key Point
                </button>
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Offer Price *
                </label>
                <input
                  type="number"
                  name="offer_price"
                  value={formData.offer_price}
                  onChange={handleInputChange}
                  placeholder="Offer Price"
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Code *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="Code"
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Type *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                >
                  <option value="">Select Type</option>
                  <option value="ORGANIZATION">ORGANIZATION</option>
                  <option value="INFLUENCER">INFLUENCER</option>
                </select>
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Duration *
                </label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                >
                  <option value="">Select Duration</option>
                  <option value="MONTHLY">MONTHLY</option>
                  <option value="YEARLY">YEARLY</option>
                </select>
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Limit
                </label>
                <input
                  type="number"
                  name="limit"
                  value={formData.limit}
                  onChange={handleInputChange}
                  placeholder="Limit (e.g., 30)"
                  className="w-full p-2 rounded"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: themes[themeName].colors.text
                  }}
                />
              </div>
              <div className="flex items-center space-x-2">
                <label
                  className="text-sm font-medium"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Active:
                </label>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, active: e.target.checked }))
                  }
                />
              </div>
            </div>
            {isLoading && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="h-2.5 rounded-full animate-pulse"
                    style={{ backgroundColor: themes[themeName].colors.primary, width: '100%' }}
                  ></div>
                </div>
                <p
                  className="text-sm mt-2"
                  style={{ color: themes[themeName].colors.textSecondary }}
                >
                  Saving subscription...
                </p>
              </div>
            )}
            {errorMessage && (
              <div
                className="mt-4 text-sm"
                style={{ color: themes[themeName].colors.error }}
              >
                {errorMessage}
              </div>
            )}
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 rounded-lg"
                style={{
                  backgroundColor: themes[themeName].colors.textMuted,
                  color: themes[themeName].colors.text
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-lg"
                style={{
                  backgroundColor: themes[themeName].colors.primary,
                  color: '#FFFFFF'
                }}
                disabled={isLoading}
              >
                {isEditing ? 'Update' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscription;
